import { ResponseCache, type IAuthenticationState } from "@/api"
import router, { Routes } from "@/router"
import { zodSchema } from "@/api"
import log from 'loglevel';
import { useSpinnerStore } from "@/stores";
import type { LocalizedString } from "typesafe-i18n";
import type { TranslationFunctions } from "@/i18n/i18n-types";
import { i18n, detectLocale } from "@/i18n/i18n-util";

export interface RequestInitExtended extends RequestInit {
  middleware?: {
    headers?: {
      doNotSetContentType?: boolean
    },
    cache?: {
      tags?: string[],
      invalidate?: string[],
      duration?: number,
      none?: boolean,
      clearAll?: boolean
    },
    spinner?: {
      skip?: boolean,
      loadingText?: (ll: TranslationFunctions) => LocalizedString
    }
  }
}

export interface IRequestMiddleware {
 process(input: RequestInfo | URL, init?: RequestInitExtended): Promise<Response>
}

export class RequestExecutor implements IRequestMiddleware {
  process(input: RequestInfo | URL, init?: RequestInitExtended): Promise<Response> {
    return fetch(input, init)
  }
}

abstract class RequestMiddlewareBase implements IRequestMiddleware {
  protected readonly next: IRequestMiddleware

  constructor(next: IRequestMiddleware) {
    this.next = next
  }

  abstract process(input: RequestInfo | URL, init?: RequestInitExtended): Promise<Response>
}

export class JsonRequestMiddleware extends RequestMiddlewareBase implements IRequestMiddleware {
  constructor(next: IRequestMiddleware) {
    super(next)
  }

  process(input: RequestInfo | URL, init?: RequestInitExtended): Promise<Response> {
    if (!init?.body || (init?.headers && ("content-type" in init.headers || init.middleware?.headers?.doNotSetContentType))) {
      return this.next.process(input, init)
    }

    return this.next.process(input, {
      ... init,
      headers: {
        ... init?.headers,
        "content-type": "application/json; charset=UTF-8"
      }
    })
  }
}

export class ExposeRefreshTokenMiddleware extends RequestMiddlewareBase implements IRequestMiddleware {
  constructor(next: IRequestMiddleware) {
    super(next)
  }

  process(input: RequestInfo | URL, init?: RequestInitExtended): Promise<Response> {
    return this.next.process(input, {
      ... init,
      credentials: 'include'
    })
  }
}

export class AuthorizationMiddleware extends RequestMiddlewareBase implements IRequestMiddleware {
  private readonly auth: IAuthenticationState

  constructor(next: IRequestMiddleware, auth: IAuthenticationState) {
    super(next)
    this.auth = auth
  }

  async process(input: RequestInfo | URL, init?: RequestInitExtended): Promise<Response> {
    return await this.next.process(input, {
      ...init,
      headers: {
        ... init?.headers,
        Authorization: `Bearer ${this.auth.getToken()}`
      }
    })
  }
}

export class BaseUrlMiddleware extends RequestMiddlewareBase implements IRequestMiddleware {
  private readonly baseUrl: string

  constructor(next: IRequestMiddleware, baseUrl: string) {
    super(next)
    this.baseUrl = baseUrl
  }

  async process(input: RequestInfo | URL, init?: RequestInitExtended): Promise<Response> {
    return this.next.process(`${this.baseUrl}${input}`, init)
  }
}

export class RefreshTokenMiddleware extends RequestMiddlewareBase implements IRequestMiddleware {
  private refreshing = false
  private readonly auth: IAuthenticationState
  private readonly refreshSubscribers: Array<() => void> = []

  constructor(next: IRequestMiddleware, auth: IAuthenticationState) {
    super(next)
    this.auth = auth
  }

  async process(input: RequestInfo | URL, init?: RequestInitExtended): Promise<Response> {
    const response = await this.next.process(input, init)

    if (this.tokenIsInvalid(response)) {
      this.signOutAndRedirectToLogin()
      return response
    }

    if (!this.refreshNeeded(response))
      return response

    if (this.refreshing) {
      return new Promise(resolve => {
        this.refreshSubscribers.push(() => this.retryRequest(input, init).then(r => resolve(r)))
      })
    }
    else {
      await this.doRefresh()
      this.notifyRefreshSubscribers()
    }

    return await this.retryRequest(input, init)
  }

  private refreshNeeded(response: Response) {
    return response.headers.has("token-expired")
  }

  private tokenIsInvalid(response: Response) {
    return response.status === 401
      && !response.headers.has("token-expired")
      && this.auth.getToken() !== undefined
  }

  private signOutAndRedirectToLogin() {
    this.auth.setToken(undefined)
    router.push(Routes.Login.GetHref())
  }

  private async doRefresh() {
    this.refreshing = true

    const response = await this.next.process('/api/member/refresh-token', {
      credentials: 'include',
    })

    this.refreshing = false

    if (response.ok) {
      const body = zodSchema.RefreshToken.parse(await response.json())
      this.auth.setToken(body.accessToken)
    }
    else {
      this.auth.setToken(undefined)
      router.push(Routes.Login.GetHref())
    }
  }

  private async retryRequest(input: RequestInfo | URL, init?: RequestInitExtended) {
    return await this.process(input, {
      ... init,
      headers: {
        ...init?.headers,
        Authorization: `Bearer ${this.auth.getToken()}`
      }
    })
  }

  private notifyRefreshSubscribers() {
    this.refreshSubscribers.forEach(callback => callback())
    this.refreshSubscribers.length = 0
  }
}

export class LogRequestMiddleware extends RequestMiddlewareBase implements IRequestMiddleware {
  constructor(next: IRequestMiddleware) {
    super(next)
  }

  async process(input: RequestInfo | URL, init?: RequestInitExtended): Promise<Response> {
    log.debug(`Request: { METHOD: ${init?.method ?? 'GET'}, URL: ${input} }`)
    const response = await this.next.process(input, init)

    if (response.ok) {
      log.debug(`Response: { METHOD: ${init?.method ?? 'GET'}, URL: ${input}, status: ${response.status} }`)
    }
    else {
      log.error(`Response: { METHOD: ${init?.method ?? 'GET'}, URL: ${input}, status: ${response.status}, statusText: ${response.statusText} }`)
    }

    return response
  }
}

export class LoadingSpinnerMiddleware extends RequestMiddlewareBase implements IRequestMiddleware {
  constructor(next: IRequestMiddleware) {
    super(next)
  }

  process(input: RequestInfo | URL, init?: RequestInitExtended): Promise<Response> {
    if (init?.middleware?.spinner?.skip) {
      return this.next.process(input, init)
    }
    else {
      const spinner = useSpinnerStore()

      return spinner.showSpinner(() => this.next.process(input, init), this.getLoadingText(init))
    }
  }

  getLoadingText(init?: RequestInitExtended) {
    if (init?.middleware?.spinner?.loadingText) {
      const locale = detectLocale()
      const translation = i18n()

      return init.middleware.spinner.loadingText(translation[locale])
    }
  }
}

export class CacheResponseMiddleware extends RequestMiddlewareBase implements IRequestMiddleware {
  private readonly cache: ResponseCache

  constructor(next: IRequestMiddleware) {
    super(next)
    this.cache = new ResponseCache()
  }

  async process(input: RequestInfo | URL, init?: RequestInitExtended): Promise<Response> {
    const method = init?.method ?? 'GET'
    const isGet = method === 'GET'
    const cacheOptions = init?.middleware?.cache
    const key = `${method} ${input.toString()}`

    if (isGet) {
      const cached = this.cache.getValue(key)

      if (cached) {
        log.debug(`Cache Middleware: { cacheHit: true, key: ${key} }`)
        return cached.clone()
      }

      log.debug(`Cache Middleware: { cacheHit: false, key: ${key} }`)
    }

    const response = await this.next.process(input, init)

    const isNotStream = !(response.headers.get('content-type')?.includes('event-stream') ?? false);

    if (isGet && response.ok && isNotStream && !cacheOptions?.none) {
      this.cache.addValue(key, response.clone(), cacheOptions?.tags ?? [], cacheOptions?.duration)
    }
    else if (!isGet) {
      if (cacheOptions?.clearAll) {
        this.cache.clear()
      } else if (cacheOptions?.invalidate?.length) {
        this.cache.invalidate(cacheOptions.invalidate)
      }
    }

    return response
  }
}
